/**
 * The core HTTP client for the pbxmgr API.
 *
 * Every call is same-origin (nginx serves the SPA and proxies `/api`), so the
 * HttpOnly session cookie flows automatically with `credentials: "include"`.
 * Mutations carry the synchronizer CSRF token in `X-CSRF-Token` (brief §2.6);
 * the token is obtained from `/api/auth/me` after login and set here.
 */

const CSRF_HEADER = "X-CSRF-Token";
const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** An error carrying the HTTP status and any parsed problem detail. */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: string | undefined;

  constructor(status: number, message: string, detail?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }

  /** Whether this error is an unauthenticated (401) response. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

let csrfToken: string | null = null;

/** Set (or clear) the CSRF token used for subsequent mutations. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/**
 * Session-expiry plumbing (one place, every request).
 *
 * The AuthProvider registers a handler; any 401 anywhere — a background
 * query, a button click, the dashboard feed's re-auth close — funnels
 * through it, so an expired session becomes an explicit "signed out"
 * state instead of a page that silently stops responding. The handler
 * itself decides whether a session actually existed (a 401 on the login
 * form or the anonymous bootstrap probe is not an expiry).
 */
let unauthenticatedHandler: (() => void) | null = null;

/** Register the app-level handler invoked whenever a request gets a 401. */
export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  unauthenticatedHandler = handler;
}

/** Invoke the 401 handler (used by non-HTTP callers like the WS feed). */
export function notifyUnauthenticated(): void {
  unauthenticatedHandler?.();
}

// When the last successful request finished — the client-side estimate of
// the server's idle clock (which restarts on every authenticated request).
// The expiry-warning banner derives its countdown from this.
let lastActivityAt = Date.now();

/** Epoch ms of the most recent successful API response. */
export function getLastActivityAt(): number {
  return lastActivityAt;
}

export interface RequestOptions {
  method?: string;
  /** JSON-serializable request body, or a FormData for multipart uploads. */
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Perform an API request and parse the JSON response.
 *
 * @throws {ApiError} On any non-2xx response, or a network failure.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const isForm = options.body instanceof FormData;
  const headers: Record<string, string> = { Accept: "application/json" };
  // FormData sets its own multipart Content-Type (with boundary).
  if (options.body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  if (MUTATING.has(method) && csrfToken) headers[CSRF_HEADER] = csrfToken;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: "include",
      body: isForm
        ? (options.body as FormData)
        : options.body !== undefined
          ? JSON.stringify(options.body)
          : undefined,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    throw new ApiError(0, "network error", cause instanceof Error ? cause.message : undefined);
  }

  if (!response.ok) {
    if (response.status === 401) notifyUnauthenticated();
    throw new ApiError(response.status, `request failed (${response.status})`, await detailOf(response));
  }
  lastActivityAt = Date.now();
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Extract a FastAPI `{detail}` message from an error response, best-effort. */
async function detailOf(response: Response): Promise<string | undefined> {
  try {
    const data: unknown = await response.json();
    if (data && typeof data === "object" && "detail" in data) {
      const detail = (data as { detail: unknown }).detail;
      return typeof detail === "string" ? detail : JSON.stringify(detail);
    }
  } catch {
    // non-JSON error body — no detail to surface
  }
  return undefined;
}
